import type { RefereeMatchRow, TournamentReferee } from "./api";
import { divisionAccentColor } from "./tournamentDivisions";

// Árbitros (Eduardo 2026-10-16, maquetas Arbitros / Arbitro-cel /
// Hoja-Control): lógica pura de asignaciones y etiquetas, probada sin montar
// las páginas.

/** Árbitros en orden alfabético (A→Z, sin acentos ni mayúsculas). */
export function sortRefereesAZ<T extends Pick<TournamentReferee, "full_name" | "user_id">>(referees: T[]): T[] {
  return [...referees].sort(
    (a, b) =>
      a.full_name.localeCompare(b.full_name, "es-MX", { sensitivity: "base" }) || a.user_id - b.user_id,
  );
}

export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

/** «Grupo A» → «A»; nombres sin la palabra se quedan igual. */
export function shortGroupName(name: string | null | undefined): string {
  const text = (name ?? "").trim();
  return text.replace(/^grupo\s+/i, "").trim();
}

/** Chip de la categoría y el grupo: «8M · A» (clave de la categoría si la
 * tiene, si no su nombre). */
export function matchChipLabel(row: Pick<RefereeMatchRow, "division_code" | "division_name" | "group_name">): string {
  const division = row.division_code?.trim() || row.division_name?.trim() || "";
  const group = shortGroupName(row.group_name);
  return [division, group].filter(Boolean).join(" · ") || "—";
}

export function matchChipColor(row: Pick<RefereeMatchRow, "division_id" | "division_name">): string {
  if (row.division_id == null) return "#9aa89a";
  return divisionAccentColor({ id: row.division_id, name: row.division_name ?? "" });
}

/** Rama de una categoría desde su género. */
export function ramaLabel(gender: string | null | undefined): string | null {
  const value = (gender ?? "").trim().toLowerCase();
  if (value === "male" || value === "varonil") return "Varonil";
  if (value === "female" || value === "femenil") return "Femenil";
  if (value === "mixed" || value === "mixta" || value === "mixto") return "Mixta";
  return null;
}

/** Rama del encabezado (Eduardo 2026-10-16: «varía por categoría»): la de
 * todas si es una sola; con dos, las dos; con más, «Varias». */
export function ramaHeader(genders: Array<string | null | undefined>): string {
  const labels = [...new Set(genders.map(ramaLabel).filter((v): v is string => v !== null))];
  if (labels.length === 0) return "—";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(", ");
  return "Varias";
}

/** "HH:MM" → minutos; null sin hora. */
function minutesOf(time: string | null | undefined): number | null {
  const found = /^(\d{1,2}):(\d{2})/.exec((time ?? "").trim());
  return found ? Number(found[1]) * 60 + Number(found[2]) : null;
}

export type DayPeriod = "all" | "morning" | "afternoon";

/** La mañana termina a las 14:00. */
export function inPeriod(time: string | null | undefined, period: DayPeriod): boolean {
  if (period === "all") return true;
  const minutes = minutesOf(time);
  if (minutes === null) return false;
  return period === "morning" ? minutes < 14 * 60 : minutes >= 14 * 60;
}

export interface HourBlock {
  time: string;
  rows: RefereeMatchRow[];
}

/** Partidos agrupados por hora (ya vienen en orden de hora y cancha). */
export function hourBlocks(rows: RefereeMatchRow[]): HourBlock[] {
  const blocks: HourBlock[] = [];
  for (const row of rows) {
    const time = row.scheduled_time?.slice(0, 5) || "";
    const last = blocks[blocks.length - 1];
    if (last && last.time === time) last.rows.push(row);
    else blocks.push({ time, rows: [row] });
  }
  return blocks;
}

/** «Por cancha»: partidos de esa cancha en el periodo, para un árbitro. */
export function courtAssignments(
  rows: RefereeMatchRow[],
  court: string,
  refereeId: number | null,
  period: DayPeriod,
): Array<{ match_id: number; referee_user_id: number | null }> {
  return rows
    .filter((row) => (row.court ?? "").trim() === court.trim() && inPeriod(row.scheduled_time, period))
    .map((row) => ({ match_id: row.id, referee_user_id: refereeId }));
}

/** «Asignar bloque»: los partidos SIN árbitro de esa hora. */
export function blockAssignments(
  block: HourBlock,
  refereeId: number,
): Array<{ match_id: number; referee_user_id: number }> {
  return block.rows
    .filter((row) => row.referee_user_id == null)
    .map((row) => ({ match_id: row.id, referee_user_id: refereeId }));
}

/** Canchas del día en orden (1, 2, …, luego las de nombre). */
export function dayCourts(rows: RefereeMatchRow[]): string[] {
  const courts = [...new Set(rows.map((row) => (row.court ?? "").trim()).filter(Boolean))];
  return courts.sort((a, b) => a.localeCompare(b, "es-MX", { numeric: true, sensitivity: "base" }));
}

/** Contraseña temporal para la invitación (el árbitro crea la suya al entrar). */
export function temporaryPassword(random: () => number = Math.random): string {
  const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 8; i += 1) out += alphabet[Math.floor(random() * alphabet.length)];
  return out;
}
